import { Component, OnInit } from '@angular/core';

import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faPalette, faPlus, faEdit, faTrash, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { TenantsAdminService } from '../../services/tenants-admin.service';
import { TenantAdmin } from '../../interfaces/tenant-admin.interface';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { ModalCreateEditTenantComponent } from '../../components/modal-create-edit-tenant/modal-create-edit-tenant.component';
import { normalizeForSearch } from '../../utils/text-search.util';

@Component({
  selector: 'app-branding',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    ItemListComponent,
    HeaderActionsComponent,
    ModalDeleteConfirmComponent,
    ModalCreateEditTenantComponent
  ],
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss']
})
export class BrandingComponent implements OnInit {
  tenants: TenantAdmin[] = [];
  filteredTenants: TenantAdmin[] = [];
  itemsForList: ListItem[] = [];
  searchTerm = '';
  loading = false;

  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;

  selectedTenant: TenantAdmin | null = null;

  faPalette = faPalette;
  faPlus = faPlus;
  faEdit = faEdit;
  faTrash = faTrash;
  faQuestionCircle = faQuestionCircle;

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BRANDING.CREATE_BUTTON',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help',
      icon: faQuestionCircle,
      visible: false,
      disabled: true
    },
    searchConfig: {
      showTextSearch: true,
      showTagFilter: false,
      textPlaceholder: 'SEARCH.PLACEHOLDER.BRANDING',
      tagPlaceholder: ''
    },
    showBackButton: true
  };

  listConfig: ListConfig = {
    itemIcon: faPalette,
    itemIconClass: 'config-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      active: 'BRANDING.STATUS.ACTIVE',
      inactive: 'BRANDING.STATUS.INACTIVE'
    },
    emptyMessage: 'BRANDING.NO_TENANTS',
    emptyMessageFiltered: 'BRANDING.NO_TENANTS_SEARCH',
    actions: [
      { id: 'edit', label: 'BUTTON.EDIT', icon: faEdit, cssClass: 'edit-item' },
      { id: 'delete', label: 'BUTTON.DELETE', icon: faTrash, cssClass: 'delete-item' }
    ]
  };

  constructor(
    private router: Router,
    private translate: TranslateService,
    private tenantsAdminService: TenantsAdminService,
    private tenantService: TenantService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.loadTenants();
  }

  loadTenants(): void {
    this.loading = true;
    this.tenantsAdminService.list(true).subscribe({
      next: (tenants) => {
        this.tenants = tenants;
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
        this.tenants = [];
        this.filteredTenants = [];
        this.itemsForList = [];
      }
    });
  }

  onTenantUpdated(): void {
    this.loadTenants();
    this.tenantService.refresh().subscribe();
  }

  onSearch(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    const term = normalizeForSearch(this.searchTerm);
    if (!term) {
      this.filteredTenants = [...this.tenants];
    } else {
      this.filteredTenants = this.tenants.filter(t =>
        normalizeForSearch(t.name).includes(term) ||
        normalizeForSearch(t.slug).includes(term)
      );
    }
    this.buildItemsForList();
  }

  private buildItemsForList(): void {
    this.itemsForList = this.filteredTenants.map(t => ({
      id: t.id,
      name: t.name || t.slug || 'Delfos Design',
      type: t.is_active ? 'active' : 'inactive'
    }));
  }

  onItemClick(item: ListItem): void {
    const tenant = this.tenants.find(t => t.id === item.id);
    if (tenant) this.openEditModal(tenant);
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const tenant = this.tenants.find(t => t.id === event.item.id);
    if (!tenant) return;
    if (event.action === 'edit') this.openEditModal(tenant);
    if (event.action === 'delete') this.openDeleteModal(tenant);
  }

  openCreateModal(): void {
    this.selectedTenant = null;
    this.showCreateModal = true;
  }

  openEditModal(tenant: TenantAdmin): void {
    this.selectedTenant = tenant;
    this.showEditModal = true;
  }

  openDeleteModal(tenant: TenantAdmin): void {
    this.selectedTenant = tenant;
    this.showDeleteModal = true;
  }

  deleteTenant(): void {
    if (!this.selectedTenant) return;
    this.tenantsAdminService.delete(this.selectedTenant.id).subscribe({
      next: () => {
        this.closeModals();
        this.onTenantUpdated();
        this.alertService.showSuccess(this.translate.instant('BRANDING.DELETE.SUCCESS'));
      },
      error: (err) => this.alertService.showError(err)
    });
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showEditModal = false;
    this.showDeleteModal = false;
    this.selectedTenant = null;
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm.trim();
  }

  onHeaderFiltersChanged(event: any): void {
    if ('searchTerm' in event) {
      this.searchTerm = event.searchTerm || '';
      this.onSearch();
    }
  }
}
